import { Container, Box, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import React, { useRef, useEffect, useState } from 'react';

export interface LineChartSkeletonProps {
  /** Number of skeleton lines to display */
  lineCount?: number;
  /** Whether to show grid lines */
  showGrid?: boolean;
  /** Whether to show legend */
  showLegend?: boolean;
  /** Chart margins */
  margin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
}

const LineChartSkeleton: React.FC<LineChartSkeletonProps> = ({
  lineCount = 3,
  showGrid = true,
  showLegend = true,
  margin = { top: 20, right: 20, bottom: 40, left: 0 },
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const chartHeight = isDesktop ? 320 : 300;
  const chartAreaHeight = chartHeight - (margin.top || 0) - (margin.bottom || 0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const lineColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.success.main,
  ];

  const generateSkeletonPath = (index: number, containerWidth: number) => {
    const points = 8;
    const chartAreaWidth = containerWidth - (margin.left || 0) - (margin.right || 0);
    const stepX = chartAreaWidth / (points - 1);

    const startY = chartAreaHeight * (0.6 + index * 0.1);
    const endY = chartAreaHeight * (0.2 + index * 0.1);
    const totalDecrease = startY - endY;

    let path = '';
    for (let i = 0; i < points; i++) {
      const x = i * stepX;
      const progress = i / (points - 1);
      const baseY = startY - (totalDecrease * progress);

      const variation = Math.sin((i * Math.PI) / (points - 1)) * (15 + index * 5);
      const y = baseY + variation;

      if (i === 0) {
        path += `M ${x} ${y}`;
      } else {
        const prevX = (i - 1) * stepX;
        const controlX = prevX + stepX / 2;
        const prevY = startY - (totalDecrease * ((i - 1) / (points - 1))) + (Math.sin(((i - 1) * Math.PI) / (points - 1)) * (15 + index * 5));
        const controlY = (prevY + y) / 2;
        path += ` Q ${controlX} ${controlY} ${x} ${y}`;
      }
    }
    return path;
  };

  return (
    <Container
      sx={{
        position: 'relative',
        height: chartHeight,
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        paddingY: 2,
        flexDirection: 'column',
      }}
    >
      {showLegend && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            mb: 1,
            mt: -1,
          }}
        >
          {Array.from({ length: lineCount }).map((_, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 2,
                  backgroundColor: lineColors[index % lineColors.length],
                  opacity: 0.3,
                  borderRadius: 1,
                }}
              />
              <Skeleton variant="text" width={60} height={16} />
            </Box>
          ))}
        </Box>
      )}

      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          position: 'relative',
          ml: 5,
          width: '100%',
        }}
      >
        {containerWidth > 0 && (() => {
          const chartAreaWidth = containerWidth - (margin.left || 0) - (margin.right || 0);
          return (
            <svg
              width="100%"
              height={chartAreaHeight}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              {showGrid && (
                <g opacity={0.1}>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <line
                      key={`h-${index}`}
                      x1={0}
                      y1={(index * chartAreaHeight) / 4}
                      x2={chartAreaWidth}
                      y2={(index * chartAreaHeight) / 4}
                      stroke={theme.palette.text.secondary}
                      strokeDasharray="5 5"
                    />
                  ))}
                </g>
              )}

              {Array.from({ length: lineCount }).map((_, index) => (
                <g key={index}>
                  <path
                    d={generateSkeletonPath(index, containerWidth)}
                    fill="none"
                    stroke={lineColors[index % lineColors.length]}
                    strokeWidth={2}
                    opacity={0.3}
                  />
                </g>
              ))}
            </svg>
          );
        })()}

        <Box
          sx={{
            position: 'absolute',
            left: -40,
            top: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            py: 1,
          }}
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="text"
              width={30}
              height={12}
              sx={{ fontSize: 10 }}
            />
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          ml: 5,
          mr: 0,
          height: 20,
        }}
      >
        <Skeleton
          variant="rectangular"
          width="100%"
          height={2}
          sx={{
            borderRadius: 1,
            opacity: 0.3,
          }}
        />
      </Box>

      <Box
        sx={{
          ml: 5,
          mr: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="text"
            width={20}
            height={12}
            sx={{ fontSize: 10 }}
          />
        ))}
      </Box>
    </Container>
  );
};

export default LineChartSkeleton; 